import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { FileText, Download, X, Loader2, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { BRAND } from "@/lib/data";

export default function Guides() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // guide being downloaded (slug)
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "Kolkata" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/guides")
      .then((r) => setGuides(r.data || []))
      .catch(() => setGuides([
        { slug: "commercial-fit-out-checklist", title: "Commercial Fit-Out Checklist", subtitle: "Everything to lock down before you sign off on an office, restaurant or retail project.", sections: 7 },
        { slug: "modular-kitchen-planning-guide", title: "Modular Kitchen Planning Guide", subtitle: "Decisions that separate a kitchen that lasts 10+ years from one that swells in 18 months.", sections: 10 },
      ]))
      .finally(() => setLoading(false));
  }, []);

  const openGate = (g) => {
    setActive(g);
    trackEvent("guide_gate_opened", { slug: g.slug });
  };

  const download = async (e) => {
    e.preventDefault();
    if (!active) return;
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone to download.");
      return;
    }
    setSending(true);
    try {
      const resp = await api.post(
        "/guides/download",
        { slug: active.slug, name: form.name, email: form.email, phone: form.phone, city: form.city },
        { responseType: "blob" }
      );
      // Trigger download
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ZEBO-${active.slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      trackEvent("guide_downloaded", { slug: active.slug });
      toast.success("Guide is downloading. Our team will be in touch shortly.");
      setActive(null);
      setForm({ name: "", email: "", phone: "", city: "Kolkata" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Download failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="guides-page" className="pt-32 pb-28 relative z-10 bg-[#F9F8F6]">
      <Seo
        title="Free Interior Design Guides — Commercial Fit-Out & Modular Kitchen | ZEBO"
        description="Download ZEBO's free planning guides — a Commercial Fit-Out Checklist and a Modular Kitchen Planning Guide. Written by our Kolkata design team."
        path="/guides"
      />

      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-[#8B0000] mb-4 flex items-center gap-2">
            <FileText size={14} /> Free planning guides
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-[#143D63] leading-[0.98]">
            Field-tested playbooks from our design team.
          </h1>
          <p className="text-[#143D63]/70 mt-6 max-w-2xl leading-relaxed">
            The exact checklists we use before starting a project — offered free so you can plan smarter. Drop your details and the PDF opens instantly.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 mt-14">
          {loading ? (
            <div className="col-span-2 flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#F8C21C]" size={30} /></div>
          ) : guides.map((g, i) => (
            <Reveal key={g.slug} delay={i * 0.06}>
              <div data-testid={`guide-card-${g.slug}`} className="bg-white border border-[#143D63]/15 h-full flex flex-col">
                <div className="bg-[#143D63] text-[#F9F8F6] p-8">
                  <p className="overline text-[#F8C21C] text-[10px] mb-3">A ZEBO GUIDE</p>
                  <h2 className="font-serif text-3xl leading-tight mb-3">{g.title}</h2>
                  <p className="text-sm text-[#F9F8F6]/75 leading-relaxed">{g.subtitle}</p>
                </div>
                <div className="p-8 flex-1 flex flex-col justify-between">
                  <p className="text-xs text-[#143D63]/60 mb-6">{g.sections} sections · PDF · Personalised for you</p>
                  <button
                    data-testid={`guide-download-${g.slug}`}
                    onClick={() => openGate(g)}
                    className="group inline-flex items-center justify-center gap-2 bg-[#F8C21C] text-[#143D63] px-6 py-3.5 font-bold text-sm hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors"
                  >
                    <Download size={16} /> Download PDF
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <p className="text-xs text-[#143D63]/50 mt-10 max-w-2xl">
          By downloading, you agree to receive an occasional email from {BRAND.full}. Unsubscribe anytime. See our{" "}
          <a href="/privacy" className="link-underline text-[#8B0000]">Privacy Policy</a>.
        </p>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => !sending && setActive(null)}
            className="fixed inset-0 z-[70] bg-[#0A0A0A]/80 flex items-center justify-center p-4"
            data-testid="guide-gate-modal"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#F9F8F6] w-full max-w-md"
            >
              <div className="bg-[#143D63] text-[#F9F8F6] px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="overline text-[#F8C21C] text-[10px]">Download</p>
                  <p className="font-serif text-lg leading-tight">{active.title}</p>
                </div>
                <button data-testid="guide-gate-close" onClick={() => !sending && setActive(null)} className="hover:text-[#F8C21C]"><X size={20} /></button>
              </div>
              <form onSubmit={download} className="p-6 space-y-3">
                <p className="text-sm text-[#143D63]/70 mb-3">Tell us who to send it to and it opens instantly.</p>
                <input data-testid="guide-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none" required />
                <input data-testid="guide-email" type="email" placeholder="you@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none" required />
                <input data-testid="guide-phone" placeholder="Phone (+91…)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none" required />
                <input data-testid="guide-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none" />
                <button data-testid="guide-submit" type="submit" disabled={sending}
                  className="w-full flex items-center justify-center gap-2 bg-[#F8C21C] text-[#143D63] px-6 py-3.5 font-bold text-sm hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors disabled:opacity-60">
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  Send me the PDF
                </button>
                <p className="text-[11px] text-[#143D63]/50 leading-relaxed">Downloads create a lead in our CRM so a designer can follow up. Not marketing spam.</p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
